import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormattedList,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from "react-intl";
import HoldersSelectMultipleGrid from "../../../../../components/Form/HoldersSelectMultipleGrid";
import FormController from "../../../../../components/ReactHookForm/FormController";
import { PRESTATIONS_PRICES_TYPES } from "../../../../../constants/prestations";
import {
  SUBSCRIPTIONS_TYPES_VALUE_PER_CUSTOMER,
  SUBSCRIPTIONS_TYPES_VALUE_PER_HOLDER,
} from "../../../../../constants/subscriptions";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Subscription").Subscription} subscription
 * @property {import("@raiden/library/types/Holder").Holder[]} holders
 *
 * @param {Props} props
 */
function Form({ subscription, holders }) {
  const intl = useIntl();

  const { control } = useFormContext();

  const holderIds = useWatch({ name: "holder_id" });

  const selectedHolders = useMemo(() => {
    return holders
      .filter((holder) => holderIds.includes(String(holder.id)))
      .map((holder) => holder.first_name);
  }, [holderIds, holders]);

  const estimatedPriceType =
    subscription.estimated_price?.type &&
    Object.keys(PRESTATIONS_PRICES_TYPES).includes(
      subscription.estimated_price.type,
    )
      ? intl.formatMessage(
          PRESTATIONS_PRICES_TYPES[subscription.estimated_price?.type].label,
        )
      : subscription.estimated_price?.type;

  return (
    <Stack spacing="1rem">
      {subscription.type === SUBSCRIPTIONS_TYPES_VALUE_PER_HOLDER && (
        <Box borderWidth="1px" p="1rem">
          <FormController
            control={control}
            name="holder_id"
            label={intl.formatMessage(
              {
                defaultMessage:
                  "Quel(s) adhérent(s) souhaitez-vous inscrire{nbsp}?",
              },
              { nbsp: "\u00A0" },
            )}
            renderWithFormControl={(field) => (
              <HoldersSelectMultipleGrid
                holders={holders}
                validator={(holder) => {
                  // on désactive le holder s'il est déjà inscrit
                  if (
                    subscription.subscribed_holders?.some(
                      (subscribedHolder) => subscribedHolder.id === holder.id,
                    )
                  ) {
                    return {
                      isDisabled: true,
                      reason: "Déjà inscrit",
                    };
                  }
                  return {
                    isDisabled: false,
                  };
                }}
                {...field}
              />
            )}
          />
        </Box>
      )}

      <Alert status="info">
        <AlertIcon />

        <AlertDescription>
          <Text>
            <FormattedMessage defaultMessage="Après confirmation de votre demande, vous pourrez retrouvez la liste de vos cotisations dans le menu “Cotisations” de votre compte." />
          </Text>
        </AlertDescription>
      </Alert>

      <Stack
        spacing="0.75rem"
        borderWidth="1px"
        borderRadius="0.375rem"
        p="0.75rem">
        <Text fontSize="1.125rem" fontWeight={400} lineHeight="1.75rem">
          <FormattedMessage defaultMessage="Récapitulatif de votre cotisation" />
        </Text>

        <Stack spacing=".5rem">
          <Text fontSize="0.875rem" fontWeight={700} lineHeight="1.25rem">
            <FormattedMessage defaultMessage="Nom" />
          </Text>

          <Text>{subscription.name}</Text>
        </Stack>

        <Stack spacing=".5rem">
          <Text fontSize="0.875rem" fontWeight={700} lineHeight="1.25rem">
            <FormattedMessage defaultMessage="Inscrit(s)" />
          </Text>

          <Text>
            {subscription.type === SUBSCRIPTIONS_TYPES_VALUE_PER_HOLDER && (
              <>
                {selectedHolders.length > 0 ? (
                  <FormattedList value={selectedHolders} />
                ) : (
                  "-"
                )}
              </>
            )}

            {subscription.type === SUBSCRIPTIONS_TYPES_VALUE_PER_CUSTOMER && (
              <FormattedMessage defaultMessage="Tous les membres du foyer" />
            )}
          </Text>
        </Stack>

        {estimatedPriceType && (
          <Stack spacing=".5rem">
            <Text fontSize="0.875rem" fontWeight={700} lineHeight="1.25rem">
              <FormattedMessage defaultMessage="Tarif appliqué" />
            </Text>

            <Text>{estimatedPriceType}</Text>
          </Stack>
        )}

        {subscription.estimated_price && (
          <Stack spacing=".5rem">
            <Text fontSize="0.875rem" fontWeight={700} lineHeight="1.25rem">
              <FormattedMessage defaultMessage="Tarif" />

              <Text>
                {subscription.type === SUBSCRIPTIONS_TYPES_VALUE_PER_HOLDER && (
                  <>
                    {subscription.estimated_price.amount && (
                      <FormattedNumber
                        value={
                          subscription.estimated_price.amount * holderIds.length
                        }
                        style="currency"
                        currency="EUR"
                      />
                    )}
                  </>
                )}

                {subscription.type ===
                  SUBSCRIPTIONS_TYPES_VALUE_PER_CUSTOMER && (
                  <FormattedNumber
                    value={subscription.estimated_price?.amount}
                    style="currency"
                    currency="EUR"
                  />
                )}
              </Text>
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default Form;
